/**
 * src/lib/voice/vocabularyService.js — VocabularyLearningService (Phase 90-01)
 *
 * Five-layer lifecycle over the fragmented vocab system:
 *
 *   Layer 1 · canonical lexicon   — listTerms / getTerm / promoteTerm
 *   Layer 2 · correction capture  — captureCorrection (→ review queue, NEVER direct)
 *   Layer 3 · runtime injection   — buildVocabHints (ChromaDB RAG, Supabase fallback)
 *   Layer 4 · entity normalization— normalizeTranscript (raw preserved, exact match)
 *   Layer 5 · review queue        — listPendingCandidates / approveCandidate / rejectCandidate / countPending
 *
 * Factory pattern — both the server (tools, handlers) and browser (Settings
 * review UI) inject their own Supabase client. Server additionally injects
 * `chromaHelpers` for real ChromaDB promotion; browser-side promotion should
 * go through the server API (promoteTerm silently no-ops ChromaDB writes when
 * chromaHelpers is null — the queue row still flips to 'approved').
 *
 * Design rule: auto-promote is forbidden. Every correction is a candidate.
 * Review queue is the single source of truth for "did the CEO bless this term".
 */

import { addTerm } from '../../../lib/utils/domain-vocabulary.js';
import { buildProvenance } from '../../../lib/rag/provenance.mjs';

// TICKET-1253 (RAG provenance R3): who approved a vocabulary term, when, from which row.
// Chroma ids stay as they were (re-upserts replace); prov_* rides in the metadata.
function vocabProvenance({ userId, termId, candidateId, term, createdAt }) {
  return buildProvenance({
    userId, actorKind: userId ? 'user' : 'system',
    sourceTable: termId ? 'domain_vocabulary' : 'vocabulary_review_queue', sourceId: termId || candidateId,
    kind: 'vocabulary_term', title: term, sourceCreatedAt: createdAt, contextType: 'vocabulary',
  }).metadata;
}

const REVIEW_QUEUE = 'vocabulary_review_queue';
const CANONICAL = 'domain_vocabulary';

/**
 * @param {object} deps
 * @param {import('@supabase/supabase-js').SupabaseClient} deps.supabase
 * @param {object} [deps.chromaHelpers] - { getCollection, upsertDocuments, queryVocabulary }
 *                  Injected for server use. Browser callers pass null — promotion
 *                  flips the queue row but skips ChromaDB upsert (server worker or
 *                  dedicated endpoint handles ChromaDB side).
 */
export function createVocabularyService({ supabase, chromaHelpers = null } = {}) {
  if (!supabase) throw new Error('createVocabularyService: supabase client required');

  // ── Layer 1 · canonical lexicon ────────────────────────────────────
  async function listTerms({ category, language, limit = 50 } = {}) {
    let q = supabase.from(CANONICAL)
      .select('id, term, category, misheard_as, definition, spanish, translations, usage_count, last_seen_at')
      .order('usage_count', { ascending: false })
      .limit(limit);
    if (category) q = q.eq('category', category);
    if (language === 'es') q = q.not('spanish', 'is', null);
    const { data, error } = await q;
    if (error) {
      console.warn('[vocabularyService] listTerms error:', error.message);
      return [];
    }
    return data || [];
  }

  async function getTerm(termId) {
    const { data, error } = await supabase
      .from(CANONICAL)
      .select('*')
      .eq('id', termId)
      .maybeSingle();
    if (error) {
      console.warn('[vocabularyService] getTerm error:', error.message);
      return null;
    }
    return data;
  }

  /**
   * Promote a candidate from vocabulary_review_queue to domain_vocabulary + ChromaDB.
   * @param {string} candidateId
   * @param {object} [opts]
   * @param {string} [opts.approvedBy] - user id
   * @param {object} [opts.edits]      - override candidate_term, misheard_as, category, language
   * @returns {Promise<{ ok: boolean, termId?: string, chromaOk?: boolean, error?: string }>}
   */
  async function promoteTerm(candidateId, opts = {}) {
    const approvedBy = opts?.approvedBy;
    const edits = opts?.edits || {};
    const { data: candidate, error: loadErr } = await supabase
      .from(REVIEW_QUEUE)
      .select('*')
      .eq('id', candidateId)
      .maybeSingle();
    if (loadErr || !candidate) {
      return { ok: false, error: loadErr?.message || 'candidate not found' };
    }

    const finalTerm = (edits.candidate_term ?? candidate.candidate_term)?.trim();
    const finalMisheard = Array.isArray(edits.misheard_as)
      ? edits.misheard_as.map(m => String(m).toLowerCase().trim()).filter(Boolean)
      : (candidate.misheard_as || []).map(m => String(m).toLowerCase()).filter(Boolean);
    const finalCategory = (edits.category ?? candidate.category) || 'custom';
    const finalLanguage = edits.language ?? candidate.language ?? 'en';

    if (!finalTerm) return { ok: false, error: 'candidate_term is empty' };

    // Does a canonical row already exist? Extend misheard_as if so, else insert.
    const { data: existing } = await supabase
      .from(CANONICAL)
      .select('id, misheard_as')
      .ilike('term', finalTerm)
      .maybeSingle();

    let termId;
    if (existing) {
      const merged = Array.from(new Set([...(existing.misheard_as || []), ...finalMisheard]));
      await supabase
        .from(CANONICAL)
        .update({ misheard_as: merged, last_seen_at: new Date().toISOString() })
        .eq('id', existing.id);
      termId = existing.id;
    } else {
      await addTerm(supabase, {
        term: finalTerm,
        category: finalCategory,
        misheard_as: finalMisheard,
        source: candidate.source || 'ceo_correction',
      });
      const { data: inserted } = await supabase
        .from(CANONICAL)
        .select('id')
        .ilike('term', finalTerm)
        .maybeSingle();
      termId = inserted?.id;
    }

    // ChromaDB promotion (best-effort, server-only).
    let chromaOk = false;
    if (chromaHelpers?.getCollection && chromaHelpers?.upsertDocuments) {
      try {
        // TICKET-229: 'vocabulary' never existed on the box; the live collection
        // is the 1536d domain-vocabulary-v2 reindex (writes are embedded by the
        // server helper — the deployed Chroma has no server-side embedder).
        const col = await chromaHelpers.getCollection('domain-vocabulary-v2');
        if (col) {
          await chromaHelpers.upsertDocuments(col.id, {
            ids: [`approved_${termId || Date.now()}_${finalTerm.toLowerCase().replace(/\s+/g, '_')}`],
            documents: [finalTerm],
            metadatas: [{
              ...vocabProvenance({ userId: approvedBy, termId, candidateId, term: finalTerm, createdAt: candidate.created_at }),
              term: finalTerm,
              category: finalCategory,
              language: finalLanguage,
              misheard_as: finalMisheard.join(','),
              source: candidate.source || 'ceo_correction',
              approved_at: new Date().toISOString(),
            }],
          });
          chromaOk = true;
        }
      } catch (err) {
        console.warn('[vocabularyService] ChromaDB promote failed:', err.message);
      }
    }

    // Flip queue row to approved.
    await supabase
      .from(REVIEW_QUEUE)
      .update({
        status: 'approved',
        reviewed_by: approvedBy || null,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', candidateId);

    return { ok: true, termId, chromaOk };
  }

  // ── Layer 2 · correction capture ───────────────────────────────────
  /**
   * Trusted correction — writes DIRECTLY to domain_vocabulary, bypassing the
   * review queue. Use ONLY for CEO-asserted corrections from the voice
   * overlay's "Fix term" inline UI or from privileged CLI jobs. Preserves the
   * review-queue design for non-trusted callers (agent chat, automated
   * suggestions) — those continue to go through captureCorrection().
   *
   * 2026-04-20 (session 5) — CEO report: "should be able to fix spelling
   * quickly and update canonical on the fly so it gets better quickly."
   * The review-queue ceremony was the friction that kept STT from learning
   * during a live voice session. The CEO is the sole curator in this single-
   * tenant app; when they tap "Fix", it IS the approval.
   *
   * @returns {Promise<{ ok: boolean, termId?: string, merged?: boolean, chromaOk?: boolean, error?: string }>}
   */
  async function captureCorrectionTrusted({ original, corrected, category, language = 'en', source = 'ceo_trusted', userId } = {}) {
    const origTrimmed = String(original || '').trim();
    const corrTrimmed = String(corrected || '').trim();
    if (!corrTrimmed) return { ok: false, error: 'corrected is required' };
    if (origTrimmed && origTrimmed.toLowerCase() === corrTrimmed.toLowerCase()) {
      return { ok: true, termId: null, merged: false, reason: 'identical' };
    }
    const origLower = origTrimmed.toLowerCase();
    const finalCategory = category || 'custom';

    const { data: existing } = await supabase
      .from(CANONICAL)
      .select('id, misheard_as')
      .ilike('term', corrTrimmed)
      .maybeSingle();

    let termId;
    let merged = false;
    if (existing) {
      const existingMisheard = (existing.misheard_as || []).map(m => String(m).toLowerCase());
      const nextMisheard = origLower && !existingMisheard.includes(origLower)
        ? Array.from(new Set([...existingMisheard, origLower]))
        : existingMisheard;
      await supabase
        .from(CANONICAL)
        .update({
          misheard_as: nextMisheard,
          last_seen_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id);
      termId = existing.id;
      merged = nextMisheard.length !== existingMisheard.length;
    } else {
      await addTerm(supabase, {
        term: corrTrimmed,
        category: finalCategory,
        misheard_as: origLower ? [origLower] : [],
        source,
      });
      const { data: inserted } = await supabase
        .from(CANONICAL)
        .select('id')
        .ilike('term', corrTrimmed)
        .maybeSingle();
      termId = inserted?.id;
    }

    // Best-effort ChromaDB upsert so the Hetzner RAG picks it up immediately.
    let chromaOk = false;
    if (chromaHelpers?.getCollection && chromaHelpers?.upsertDocuments && termId) {
      try {
        // TICKET-229: same collection fix as the approve path above.
        const col = await chromaHelpers.getCollection('domain-vocabulary-v2');
        if (col) {
          await chromaHelpers.upsertDocuments(col.id, {
            ids: [`trusted_${termId}_${corrTrimmed.toLowerCase().replace(/\s+/g, '_')}`],
            documents: [corrTrimmed],
            metadatas: [{
              ...vocabProvenance({ userId, termId, term: corrTrimmed }),
              term: corrTrimmed,
              category: finalCategory,
              language,
              misheard_as: origLower || '',
              source,
              approved_at: new Date().toISOString(),
            }],
          });
          chromaOk = true;
        }
      } catch (err) {
        console.warn('[vocabularyService] captureCorrectionTrusted ChromaDB upsert failed:', err.message);
      }
    }

    return { ok: true, termId, merged, chromaOk };
  }

  /**
   * Write a correction to the review queue. Never writes canonical directly.
   * @returns {Promise<{ status: 'new_candidate'|'duplicate'|'already_canonical', candidateId?: string }>}
   */
  async function captureCorrection({ original, corrected, context, source, language = 'en', category } = {}) {
    const origTrimmed = String(original || '').trim();
    const corrTrimmed = String(corrected || '').trim();
    if (!origTrimmed || !corrTrimmed) {
      return { status: 'new_candidate', error: 'empty_input' };
    }
    if (origTrimmed.toLowerCase() === corrTrimmed.toLowerCase()) {
      return { status: 'already_canonical', reason: 'identical' };
    }
    if (!source) throw new Error('captureCorrection: source is required');

    const origLower = origTrimmed.toLowerCase();

    // Already canonical? (term exists AND mishearing already recorded)
    const { data: existingTerm } = await supabase
      .from(CANONICAL)
      .select('id, misheard_as')
      .ilike('term', corrTrimmed)
      .maybeSingle();

    if (existingTerm) {
      const existingMisheard = (existingTerm.misheard_as || []).map(m => String(m).toLowerCase());
      if (existingMisheard.includes(origLower)) {
        return { status: 'already_canonical', termId: existingTerm.id };
      }
      // Term is canonical but this mishearing is new — still goes to queue, not inline write.
    }

    // Pending candidate with same term+mishearing?
    const { data: pending } = await supabase
      .from(REVIEW_QUEUE)
      .select('id, misheard_as')
      .ilike('candidate_term', corrTrimmed)
      .eq('status', 'pending')
      .maybeSingle();

    if (pending) {
      const pendingMisheard = (pending.misheard_as || []).map(m => String(m).toLowerCase());
      if (pendingMisheard.includes(origLower)) {
        return { status: 'duplicate', candidateId: pending.id };
      }
      // Same term, new mishearing — append to existing candidate's misheard_as.
      const merged = Array.from(new Set([...pendingMisheard, origLower]));
      await supabase
        .from(REVIEW_QUEUE)
        .update({ misheard_as: merged })
        .eq('id', pending.id);
      return { status: 'new_candidate', candidateId: pending.id, merged: true };
    }

    // Insert new candidate.
    const { data: inserted, error } = await supabase
      .from(REVIEW_QUEUE)
      .insert({
        candidate_term: corrTrimmed,
        misheard_as: [origLower],
        category: category || null,
        language,
        source,
        context: context || null,
      })
      .select('id')
      .maybeSingle();

    if (error) {
      console.warn('[vocabularyService] captureCorrection insert failed:', error.message);
      return { status: 'new_candidate', error: error.message };
    }

    return { status: 'new_candidate', candidateId: inserted?.id };
  }

  // ── Layer 3 · runtime injection ────────────────────────────────────
  /**
   * Assemble top vocab hints for next voice turn. ChromaDB first (context-aware),
   * Supabase fallback on failure/timeout. Never throws.
   * @returns {Promise<Array<{ term: string, misheard_as?: string[], language?: string }>>}
   */
  async function buildVocabHints({ projectType, vesselType, language = 'en', categories, contextHint, limit = 15, timeoutMs = 500 } = {}) {
    const context = { project_type: projectType, vessel_type: vesselType, categories, context_hint: contextHint };

    // Try ChromaDB path with timeout.
    if (chromaHelpers?.queryVocabulary) {
      try {
        const rag = await withTimeout(chromaHelpers.queryVocabulary(context), timeoutMs);
        if (rag?.termMap) {
          const hints = [];
          for (const [, term] of rag.termMap) {
            hints.push({ term, language });
            if (hints.length >= limit) break;
          }
          if (hints.length) return hints;
        }
      } catch (err) {
        console.warn('[vocabularyService] buildVocabHints RAG failed, falling back:', err.message);
      }
    }

    // Supabase fallback.
    //
    // 2026-04-20 (session 5) — reserve half the hint budget for category
    // ='project' terms so live project names always reach Whisper's prompt
    // regardless of usage_count ordering. Prior code pulled a single
    // top-usage_count list, which dropped new / cold projects (the 2026-
    // 04-20 "MULUC couldn't be caught" CEO report). Projects are the
    // highest-signal domain terms the CEO speaks; they must not be crowded
    // out by dormant ChatGPT-export vocabulary.
    try {
      const projectBudget = Math.max(1, Math.min(Math.floor(limit / 2), 8));
      const { data: projectRows } = await supabase
        .from(CANONICAL)
        .select('term, misheard_as, category, spanish')
        .eq('category', 'project')
        .order('usage_count', { ascending: false })
        .limit(projectBudget);

      const { data: topRows, error } = await supabase
        .from(CANONICAL)
        .select('term, misheard_as, category, spanish')
        .order('usage_count', { ascending: false })
        .limit(limit);
      if (error) {
        console.warn('[vocabularyService] buildVocabHints Supabase fallback error:', error.message);
        return [];
      }

      // Merge: project hints first, then top-usage terms, dedup by lowercase
      // term, cap at limit.
      const seen = new Set();
      const out = [];
      const push = (r) => {
        if (!r?.term) return;
        const key = String(r.term).toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        out.push({
          term: r.term,
          misheard_as: r.misheard_as || [],
          category: r.category || null,
          language: language === 'es' && r.spanish ? 'es' : 'en',
        });
      };
      for (const r of projectRows || []) push(r);
      for (const r of topRows || []) {
        if (out.length >= limit) break;
        push(r);
      }
      return out.slice(0, limit);
    } catch (err) {
      console.warn('[vocabularyService] buildVocabHints fallback threw:', err.message);
      return [];
    }
  }

  // ── Layer 4 · entity normalization ─────────────────────────────────
  /**
   * Replace known mishearings with canonical terms in transcript text. Exact
   * word-boundary match only — confidence=1.0 for these. Sub-threshold fuzzy
   * replacement is handled separately by vectorCorrect in stt.js (not here).
   *
   * @returns {{ raw: string, normalized: string, replacements: Array<{from:string, to:string, confidence:number}> }}
   */
  function normalizeTranscript({ rawTranscript, vocabHints, confidenceThreshold = 0.8 } = {}) {
    const raw = String(rawTranscript || '');
    if (!raw || !Array.isArray(vocabHints) || vocabHints.length === 0) {
      return { raw, normalized: raw, replacements: [] };
    }

    const replacements = [];
    let normalized = raw;

    for (const hint of vocabHints) {
      const term = (hint && hint.term) ? String(hint.term) : null;
      if (!term) continue;
      const misheardList = Array.isArray(hint.misheard_as) ? hint.misheard_as : [];
      for (const misheard of misheardList) {
        const m = String(misheard || '').trim();
        if (!m || m.toLowerCase() === term.toLowerCase()) continue;
        // Exact-match replacements are deterministic — confidence 1.0. Threshold
        // check is reserved for future vector-confidence paths.
        if (1.0 < confidenceThreshold) continue;
        const regex = new RegExp(`\\b${escapeRegex(m)}\\b`, 'gi');
        if (regex.test(normalized)) {
          normalized = normalized.replace(regex, term);
          replacements.push({ from: m, to: term, confidence: 1.0 });
        }
      }
    }

    return { raw, normalized, replacements };
  }

  // ── Layer 5 · review queue ─────────────────────────────────────────
  async function listPendingCandidates({ limit = 50 } = {}) {
    const { data, error } = await supabase
      .from(REVIEW_QUEUE)
      .select('id, candidate_term, misheard_as, category, language, source, context, created_at')
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) {
      console.warn('[vocabularyService] listPendingCandidates error:', error.message);
      return [];
    }
    return data || [];
  }

  async function approveCandidate(id, opts = {}) {
    return promoteTerm(id, opts);
  }

  async function rejectCandidate(id, { reason, rejectedBy } = {}) {
    const { error } = await supabase
      .from(REVIEW_QUEUE)
      .update({
        status: 'rejected',
        review_reason: reason || null,
        reviewed_by: rejectedBy || null,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', id);
    if (error) {
      console.warn('[vocabularyService] rejectCandidate error:', error.message);
      return { ok: false, error: error.message };
    }
    return { ok: true };
  }

  async function countPending() {
    const { count, error } = await supabase
      .from(REVIEW_QUEUE)
      .select('id', { count: 'exact', head: true })
      .eq('status', 'pending');
    if (error) {
      console.warn('[vocabularyService] countPending error:', error.message);
      return 0;
    }
    return count || 0;
  }

  return {
    // Layer 1
    listTerms, getTerm, promoteTerm,
    // Layer 2
    captureCorrection, captureCorrectionTrusted,
    // Layer 3
    buildVocabHints,
    // Layer 4
    normalizeTranscript,
    // Layer 5
    listPendingCandidates, approveCandidate, rejectCandidate, countPending,
  };
}

// ── Helpers ────────────────────────────────────────────────────────────

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Phase 76-03 — Synchronous alias-map normalizer.
 *
 * Replaces known mishearings/aliases with their canonical term in `text`
 * using a pre-built lowercase alias map. Pure, synchronous, zero I/O — safe
 * to call on every voice turn in the voice.chat hot path.
 *
 * The alias map is the one produced by `knowledgePacket.vocabulary_alias_map`:
 *   Map<lowercaseAlias, { canonical, entity_type?, entity_id? }>
 *
 * Word-boundary exact match (case-insensitive). Preserves entity_id for the
 * later tool-args alias pre-pass in entityResolver.js.
 *
 * @param {string} text  Raw (already server-STT'd) transcript
 * @param {Map<string, { canonical: string, entity_type?: string, entity_id?: string }>} vocabMap
 * @returns {{ normalized: string, replacements: Array<{ from: string, to: string, entity_type?: string, entity_id?: string, confidence: number }> }}
 */
export function normalizeTranscriptSync(text, vocabMap) {
  const raw = String(text || '');
  if (!raw || !vocabMap || typeof vocabMap.entries !== 'function' || vocabMap.size === 0) {
    return { normalized: raw, replacements: [] };
  }

  let normalized = raw;
  const replacements = [];
  // Longest-alias-first so "main sail" wins over "main" when both are registered.
  const entries = Array.from(vocabMap.entries()).sort((a, b) => b[0].length - a[0].length);

  for (const [alias, meta] of entries) {
    const aliasStr = String(alias || '').trim();
    if (!aliasStr) continue;
    const canonical = meta?.canonical ? String(meta.canonical) : '';
    if (!canonical || aliasStr.toLowerCase() === canonical.toLowerCase()) continue;
    const regex = new RegExp(`\\b${escapeRegex(aliasStr)}\\b`, 'gi');
    if (regex.test(normalized)) {
      normalized = normalized.replace(regex, canonical);
      replacements.push({
        from: aliasStr,
        to: canonical,
        entity_type: meta?.entity_type || null,
        entity_id: meta?.entity_id || null,
        confidence: 1.0,
      });
    }
  }

  return { normalized, replacements };
}

/**
 * Build a synchronous alias map from domain_vocabulary rows for
 * normalizeTranscriptSync. Maps each lowercased misheard variant ->
 * { canonical, entity_type, entity_id }. Pure; the client reads
 * domain_vocabulary directly (RLS allows authenticated SELECT) and feeds the
 * rows here so the streaming-STT final gets the same canonical-term pass the
 * batch path gets server-side (stt.js applyCorrections). CARL development-010.
 *
 * @param {Array<{term:string, misheard_as?:string[], category?:string, id?:string}>} rows
 * @returns {Map<string,{canonical:string, entity_type:string|null, entity_id:string|null}>}
 */
export function buildCorrectionMap(rows) {
  const map = new Map();
  if (!Array.isArray(rows)) return map;
  for (const row of rows) {
    const canonical = row?.term;
    if (!canonical || !Array.isArray(row.misheard_as)) continue;
    for (const mis of row.misheard_as) {
      const key = String(mis || '').toLowerCase().trim();
      if (key && key !== String(canonical).toLowerCase()) {
        map.set(key, { canonical, entity_type: row.category || null, entity_id: row.id || null });
      }
    }
  }
  return map;
}

/**
 * TICKET-095 — optimistic in-session learning. Insert a just-confirmed
 * correction pair into a live alias map (the one normalizeTranscriptSync
 * consumes) so the correction is effective on the NEXT utterance without
 * waiting for a refetch/reload. Never clobbers an existing richer entry
 * (one carrying entity_type/entity_id from domain_vocabulary).
 *
 * @param {Map<string, { canonical: string }>} map - live correction map (mutated)
 * @param {{ original?: string, corrected?: string }} pair
 * @returns {Map} the same map
 */
export function insertCorrectionIntoMap(map, { original, corrected } = {}) {
  if (!map || typeof map.set !== 'function') return map;
  const alias = String(original || '').trim().toLowerCase();
  const canonical = String(corrected || '').trim();
  if (!alias || !canonical || alias === canonical.toLowerCase()) return map;
  if (!map.has(alias)) {
    map.set(alias, { canonical, entity_type: null, entity_id: null });
  }
  return map;
}

function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const id = setTimeout(() => reject(new Error(`timeout ${ms}ms`)), ms);
    Promise.resolve(promise).then(
      (v) => { clearTimeout(id); resolve(v); },
      (e) => { clearTimeout(id); reject(e); },
    );
  });
}

/**
 * Default server-side chroma helpers. Dynamic imports carry @vite-ignore so the
 * client bundle does not pull in server-only deps.
 */
export function makeServerChromaHelpers() {
  return {
    async getCollection(name) {
      const mod = await import(/* @vite-ignore */ '../../../lib/utils/chroma-client.js');
      return mod.getCollection(name);
    },
    async upsertDocuments(collectionId, payload) {
      // TICKET-229: the deployed Chroma has no server-side embedder — text-only
      // upserts fail (422). Embed here (guarded: kill-switch + cost row) and
      // write WITH embeddings; the helper API stays the same for callers.
      const [chroma, embeddings] = await Promise.all([
        import(/* @vite-ignore */ '../../../lib/utils/chroma-client.js'),
        import(/* @vite-ignore */ '../../../lib/utils/openai-embeddings.js'),
      ]);
      const vectors = await embeddings.embedOpenAI(payload.documents, {
        action: 'vocab_promote', signal: AbortSignal.timeout(3000),
      });
      return chroma.upsertWithEmbeddings(collectionId, { ...payload, embeddings: vectors });
    },
    async queryVocabulary(context) {
      const mod = await import(/* @vite-ignore */ '../../../lib/utils/voice-rag.js');
      return mod.fetchContextVocabulary(context);
    },
  };
}

/**
 * Convenience: build a server-side service using agent/services/supabase.js and
 * real ChromaDB helpers. Safe to call only from Node.
 */
let _serverInstance = null;
export async function getServerVocabularyService() {
  if (_serverInstance) return _serverInstance;
  const { default: supabase } = await import(/* @vite-ignore */ '../../../agent/services/supabase.js');
  _serverInstance = createVocabularyService({
    supabase,
    chromaHelpers: makeServerChromaHelpers(),
  });
  return _serverInstance;
}
